import React from 'react';
import classNames from 'classnames';

interface SVGTitle {
  title?: string;
}

export interface IRCIconProps {
  /**
   * By default, the icon takes up the full width of its parent container.
   * Set to `true` to use the icon inline with text; in that case, the parent container\'s font size will control the icon size.',
  */
  inline?: boolean;
  /**
   * For accessibility. Leave this blank for decorative icons, but add a descriptive verb for interactive icons.',
  */
  title?: string;
  /** This is identical to `inline` except it renders at the native SVG size (28px).
   * We're probably deprecating this; size your icons via the parent container width instead. */
  nativeSize?: boolean;
  /**
   * Import the SVG with `?component` in the import path, and pass it through here.
   */
  svgComponent: (props: (React.SVGProps<SVGElement> & SVGTitle)) => React.ReactSVGElement; // a specific svg imported from /packages/cadence/icons
}

/**
 * Our default icon component. Pass it an SVG, and it handles the SVG output as well as proper ARIA / accessibility markup around it.
*/
export function RCIcon({
  inline = false,
  nativeSize = false,
  title = undefined,
  svgComponent,
}: IRCIconProps) {
  const WrappedSVGComponent = svgComponent;

  const classes = classNames('rc-icon', {
    'rc-icon--inline': inline,
    'rc-icon--native-size': nativeSize,
  });

  // if there is no accessible title, then the icon is decorative and should be
  // hidden from screen readers
  const ariaHidden = !title ? true : undefined;

  return (
    <span className={classes}>
      <WrappedSVGComponent title={title} aria-hidden={ariaHidden} />
    </span>
  );
}
